import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { motion, AnimatePresence } from "framer-motion";
import {
  FiMail,
  FiLock,
  FiArrowLeft,
  FiRefreshCw,
} from "react-icons/fi";
import { FaEye, FaEyeSlash } from "react-icons/fa";
import { CgSpinner } from "react-icons/cg";
import { TextField } from "@mui/material";
import { generateSixDigitCode, toAlphanumeric } from "../../helpers/util";
import {
  checkUserBusiness,
  sendForgetEmail,
  updateUser,
} from "../../services/auth.service";

const ForgotPassword = ({ onBack, theme = "light" }) => {
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [email, setEmail] = useState("");
  const [otp, setOtp] = useState(["", "", "", "", "", ""]);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpError, setOtpError] = useState(false);
  const [isOtpVerified, setIsOtpVerified] = useState(false);
  const [originalOtp, setOriginalOtp] = useState(null);
  const [userUuid, setUserUuid] = useState(null);
  const [showeyePassword, setShoweyePassword] = useState(false);
  const [showeyeconfirmPassword, setShoweyeconfirmPassword] = useState(false);
  const [emailError, setEmailError] = useState("");

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 10 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" }
    }
  };

  const staggerChildren = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.08
      }
    }
  };

  const getTextFieldSx = (theme) => ({
    "& .MuiInputLabel-root": {
      color: theme === "dark" ? "#9ca3af" : undefined,
    },
    "& .MuiOutlinedInput-root": {
      borderRadius: "12px",
      backgroundColor: theme === "dark" ? "#374151" : "#f9fafb",
      color: theme === "dark" ? "#f3f4f6" : "#111827",
      "& fieldset": {
        borderColor: theme === "dark" ? "#4b5563" : "#d1d5db",
      },
      "&:hover fieldset": {
        borderColor: "#3b82f6",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#3b82f6",
        borderWidth: "2px",
      },
    },
    "& .MuiFormHelperText-root": {
      color: theme === "dark" ? "#ef4444" : undefined,
    },
  });

  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      return "Email is required";
    } else if (!emailRegex.test(email)) {
      return "Enter a valid email";
    }
    return "";
  };

  const handleVerifyEmail = async () => {
    const emailValidationError = validateEmail(email);
    if (emailValidationError) {
      setEmailError(emailValidationError);
      return;
    }

    setLoading(true);
    const hardcodedOtp = `${generateSixDigitCode()}`;
    setOriginalOtp(hardcodedOtp);
    const numericValueOtp = parseFloat(hardcodedOtp);

    try {
      const emailCheckResponse = await checkUserBusiness({ email });

      if (emailCheckResponse.data?.hasBusiness) {
        const { user } = emailCheckResponse.data;
        setUserUuid(user.userUuid);
        let result = await sendForgetEmail({
          email,
          code: toAlphanumeric(numericValueOtp),
        });

        if (result.status === 200) {
          toast.success("OTP sent to your email!");
          setStep(2);
        } else {
          toast.error("Failed to send OTP! Please try again.");
        }
      } else {
        toast.error("Email is not registered!");
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShoweyePassword((prev) => !prev);
  };

  const toggleconfirmPasswordVisibility = () => {
    setShoweyeconfirmPassword((prev) => !prev);
  };

  useEffect(() => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length === 6) {
      if (enteredOtp === originalOtp) {
        setIsOtpVerified(true);
        setStep(3);
        setOtpError(false);
        toast.success("OTP verified successfully!");
      } else {
        setOtpError(true);
        toast.error("Wrong OTP! Please try again.");
      }
    }
  }, [originalOtp, otp]);

  const handleResetPassword = async () => {
    if (newPassword && confirmPassword && newPassword === confirmPassword) {
      setLoading(true);
      try {
        const result = await updateUser(userUuid, { password: newPassword });
        if (result?.status === 200 || result?.status === 201) {
          toast.success("Password reset successfully!");
          setTimeout(() => {
            onBack();
            resetForm();
          }, 1500);
        }
      } catch (error) {
        toast.error("Failed to reset password. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  const handleOtpChange = (e, index) => {
    const newOtp = [...otp];
    const value = e.target.value;

    if (!/^\d*$/.test(value)) return;

    newOtp[index] = value.slice(0, 1);
    setOtp(newOtp);

    if (value && index < 5) {
      document.getElementById(`otp-input-${index + 1}`).focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      document.getElementById(`otp-input-${index - 1}`).focus();
    }
  };

  const resetForm = () => {
    setStep(1);
    setEmail("");
    setOtp(["", "", "", "", "", ""]);
    setNewPassword("");
    setConfirmPassword("");
    setIsOtpVerified(false);
    setEmailError("");
    setOtpError(false);
  };

  const handleBack = () => {
    resetForm();
    onBack();
  };

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (emailError) setEmailError(validateEmail(value));
  };

  return (
    <div className="w-full">
      {/* Step Indicator */}
      <div className="flex justify-center mb-8">
        <div className="flex items-center space-x-3">
          {[1, 2, 3].map((stepNum) => (
            <React.Fragment key={stepNum}>
              <motion.div
                initial={false}
                animate={{
                  backgroundColor: step >= stepNum ? "#2563eb" : (theme === "dark" ? "#374151" : "#f3f4f6"),
                  scale: step === stepNum ? 1.1 : 1
                }}
                className={`flex items-center justify-center w-10 h-10 rounded-full border-2 text-sm font-bold shadow-sm ${step >= stepNum
                  ? 'border-blue-600 text-white'
                  : theme === 'dark'
                    ? 'border-gray-600 text-gray-500'
                    : 'border-gray-200 text-gray-400'
                  }`}
              >
                {stepNum}
              </motion.div>
              {stepNum < 3 && (
                <div className={`w-12 h-0.5 rounded-full ${step > stepNum
                  ? 'bg-blue-600'
                  : theme === 'dark'
                    ? 'bg-gray-700'
                    : 'bg-gray-200'
                  }`} />
              )}
            </React.Fragment>
          ))}
        </div>
      </div>

      <AnimatePresence mode='wait'>
        {/* Step 1: Email Input */}
        {step === 1 && (
          <motion.div
            key="step1"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={staggerChildren}
            className="space-y-6"
          >
            <motion.div variants={fadeInUp}>
              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={handleEmailChange}
                error={!!emailError}
                helperText={emailError}
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <FiMail className="mr-2 text-gray-400" />
                  ),
                }}
                sx={getTextFieldSx(theme)}
              />
            </motion.div>

            <motion.button
              variants={fadeInUp}
              onClick={handleVerifyEmail}
              disabled={loading || !!emailError || !email}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 ${loading || !!emailError || !email
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                }`}
            >
              {loading ? (
                <>
                  <CgSpinner className="animate-spin text-2xl" />
                  <span>Sending Code...</span>
                </>
              ) : (
                <>
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FiMail className="text-xl" />
                  </div>
                  <span>Send Reset Code</span>
                </>
              )}
            </motion.button>
          </motion.div>
        )}

        {/* Step 2: OTP Verification */}
        {step === 2 && !isOtpVerified && (
          <motion.div
            key="step2"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={staggerChildren}
            className="space-y-8"
          >
            <motion.div variants={fadeInUp} className="text-center">
              <div className={`inline-flex p-4 rounded-2xl mb-4 ${theme === "dark" ? "bg-blue-900/30" : "bg-blue-50"}`}>
                <FiLock className="text-3xl text-blue-600" />
              </div>
              <p className={`text-base ${theme === "dark" ? "text-gray-300" : "text-gray-600"}`}>
                Enter code sent to <span className="font-bold text-blue-600">{email}</span>
              </p>
            </motion.div>

            <motion.div variants={fadeInUp} className="flex justify-center gap-3">
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength="1"
                  value={digit}
                  onChange={(e) => handleOtpChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  id={`otp-input-${index}`}
                  className={`w-14 h-14 text-center text-2xl font-bold rounded-xl border-2 transition-all shadow-sm ${theme === "dark"
                    ? "bg-gray-700 border-gray-600 text-gray-100 placeholder-gray-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-900/30"
                    : "bg-white border-blue-200 text-gray-900 focus:border-blue-500 focus:ring-4 focus:ring-blue-100"
                    } ${otpError ? "border-red-500 focus:ring-red-100 dark:focus:ring-red-900/30" : ""}`}
                />
              ))}
            </motion.div>

            {otpError && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl text-sm text-center font-medium ${theme === "dark"
                  ? "bg-red-900/20 border border-red-800/30 text-red-300"
                  : "bg-red-50 border border-red-200 text-red-600"
                  }`}
              >
                Incorrect code. Please check and try again.
              </motion.div>
            )}

            <motion.div variants={fadeInUp} className="text-center">
              <button
                onClick={handleVerifyEmail}
                className={`text-sm font-semibold transition-colors hover:underline ${theme === "dark" ? "text-blue-300 hover:text-blue-200" : "text-blue-600 hover:text-blue-700"
                  }`}
              >
                Didn't receive code? Resend
              </button>
            </motion.div>
          </motion.div>
        )}

        {/* Step 3: New Password */}
        {step === 3 && isOtpVerified && (
          <motion.div
            key="step3"
            initial="hidden"
            animate="visible"
            exit="hidden"
            variants={staggerChildren}
            className="space-y-6"
          >
            <motion.div variants={fadeInUp}>
              <TextField
                fullWidth
                label="New Password"
                type={showeyePassword ? "text" : "password"}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <FiLock className="mr-2 text-gray-400" />
                  ),
                  endAdornment: (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      type="button"
                      onClick={togglePasswordVisibility}
                      className={`p-1 ${theme === "dark" ? "text-gray-400 hover:text-blue-300" : "text-gray-400 hover:text-blue-600"}`}
                    >
                      {showeyePassword ? <FaEyeSlash /> : <FaEye />}
                    </motion.button>
                  ),
                }}
                sx={getTextFieldSx(theme)}
              />
            </motion.div>

            <motion.div variants={fadeInUp}>
              <TextField
                fullWidth
                label="Confirm Password"
                type={showeyeconfirmPassword ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                variant="outlined"
                size="medium"
                InputProps={{
                  startAdornment: (
                    <FiLock className="mr-2 text-gray-400" />
                  ),
                  endAdornment: (
                    <motion.button
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                      type="button"
                      onClick={toggleconfirmPasswordVisibility}
                      className={`p-1 ${theme === "dark" ? "text-gray-400 hover:text-blue-300" : "text-gray-400 hover:text-blue-600"}`}
                    >
                      {showeyeconfirmPassword ? <FaEyeSlash /> : <FaEye />}
                    </motion.button>
                  ),
                }}
                sx={getTextFieldSx(theme)}
                error={confirmPassword && newPassword !== confirmPassword}
                helperText={confirmPassword && newPassword !== confirmPassword ? "Passwords do not match" : ""}
              />
            </motion.div>

            <motion.button
              variants={fadeInUp}
              onClick={handleResetPassword}
              disabled={loading || !newPassword || !confirmPassword || newPassword !== confirmPassword}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`w-full py-4 rounded-xl font-semibold text-lg transition-all flex items-center justify-center gap-3 mt-4 ${loading || !newPassword || !confirmPassword || newPassword !== confirmPassword
                ? 'bg-gray-200 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl'
                }`}
            >
              {loading ? (
                <>
                  <CgSpinner className="animate-spin text-2xl" />
                  <span>Resetting...</span>
                </>
              ) : (
                <>
                  <div className="p-2 bg-white/20 rounded-lg">
                    <FiRefreshCw className="text-xl" />
                  </div>
                  <span>Reset Password</span>
                </>
              )}
            </motion.button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Back Link */}
      <motion.button
        variants={fadeInUp}
        type="button"
        onClick={handleBack}
        whileHover={{ x: -5 }}
        className={`w-full mt-8 py-2 font-semibold text-sm transition-all flex items-center justify-center gap-2 group ${theme === "dark" ? "text-gray-400 hover:text-gray-300" : "text-gray-600 hover:text-gray-900"
          }`}
      >
        <FiArrowLeft className="group-hover:translate-x-[-2px] transition-transform" />
        <span>Back to Login</span>
      </motion.button>
    </div>
  );
};

export default ForgotPassword;
